import { Button } from "@/components/common/Button";
import Input from "@/components/common/Input";
import { useAuthStore } from "@/modules/auth";
import { putAuthEmail } from "@/modules/auth/api/user/auth";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type VerificationCodeProps = {
  email: string;
  onBack: () => void;
  resendCooldown: number;
  onResend: () => void;
};

const VerificationCode = ({
  email,
  onBack,
  resendCooldown,
  onResend,
}: VerificationCodeProps) => {
  const { t } = useTranslation();

  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const setAccessToken = useAuthStore((state) => state.setAccessToken);

  const handleSubmit = async () => {
    if (code.length !== 6 || isLoading) return;

    setIsLoading(true);
    const res = await putAuthEmail({ email, code });
    setIsLoading(false);

    if (res?.accessToken && res?.refreshToken) {
      setAccessToken(res.accessToken);
    }
  };

  return (
    <>
      <div className="w-full shrink-0 flex items-center justify-start px-4 py-3">
        <Button
          onClick={onBack}
          text={t("auth.verification.back")}
          size="small"
          mode="bezeled"
          pilled
        />
      </div>
      <div className="flex-1 w-full flex flex-col items-center justify-center gap-8 px-4">
        <div className="w-full flex flex-col">
          <p className="text-[1.0625rem] leading-[1.375rem] font-semibold text-center">
            {t("auth.verification.title")}
          </p>
          <p className="p-4 pb-2 text-[0.8125rem] leading-[1rem] text-section-header-text tracking-tighter uppercase">
            {t("auth.verification.label")}
          </p>
          <Input
            placeholder="• • • • • •"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            inputMode="numeric"
            maxLength={6}
          />
          <p className="px-4 pt-4 text-[0.8125rem] leading-[1rem] text-section-header-text tracking-tighter text-center">
            {t("auth.verification.description")} {email}
          </p>
        </div>
        <div className="w-full flex flex-col gap-3">
          <Button
            onClick={handleSubmit}
            text={t("auth.verification.button")}
            size="large"
            className="w-full"
            disabled={code.length !== 6}
            loading={isLoading}
          />
          <Button
            onClick={onResend}
            mode="bezeled"
            text={
              resendCooldown > 0
                ? t("auth.timer", { seconds: resendCooldown })
                : t("auth.verification.resend")
            }
            size="large"
            className="w-full"
          />
        </div>
      </div>
    </>
  );
};

export default VerificationCode;
